import * as React from 'react'

import { Box, Button, Icon, Tag, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type { TentacleErrorData } from 'modules/core/lib/parseErrorData'

import { ShareReportsPolicy } from 'modules/reports/components/DashboardShow/types'
import {
  useReportExportCsv,
  useReportExportExcel,
  useReportExportTxt,
} from 'modules/reports/hooks/useReportExport'
import type ReportStruct from 'modules/reports/lib/ReportStruct'
import { hasDeprecatedPath } from 'modules/reports/lib/projectionHelper'
import reports from 'modules/reports/store/collections/reports'
import type { ContentError } from 'modules/reports/store/models/Content'
import Report, { type Variable } from 'modules/reports/store/models/Report'
import type { ResourceData } from 'modules/reports/store/models/Resource'

import TooltipInfo from './TooltipInfo'
import './index.styles.css'
interface Props {
  reportStruct: ReportStruct<Report>
  data?: ResourceData
  canEditDashboard: boolean
  canEditReport: boolean
  onMove: () => void
  onShare: () => void
  variables: Variable[]
  lastUpdated: Date | null | undefined
  error?: Error | TentacleErrorData | ContentError
  reportsSharePolicy: ShareReportsPolicy[]
}

const useOptions = (
  reportStruct: ReportStruct<Report>,
  canEditReport: boolean,
  canEditDashboard: boolean,
  onDownloadCsv: () => void,
  onDownloadExcel: () => void,
  onDownloadTxt: () => void,
  onMove: () => void,
  onRemove: () => void,
  onShare: () => void,
  reportsSharePolicy: ShareReportsPolicy[],
  currentUserId: number
) => {
  const { navigation } = useNavigation()
  const report = reportStruct.props.resource

  const canShareReport = useUnsafeMemo(() => {
    const node = reportsSharePolicy.find((n) => n.id == report.id)

    if (!node) return false
    return node._policies?.share.can ?? false
  }, [reportsSharePolicy, report.id])

  const isSqlType = report.sqlQuery != null
  const { featureAvailability } = useFeatureAvailability(Features.analytics_advanced_reports)
  const options: React.ReactElement[] = []

  if (featureAvailability.available && canEditReport) {
    const editRoute = isSqlType
      ? navigation.reports.id.sqlEditor.fullPath({ id: report.id })
      : navigation.reports.id.edit.fullPath({ id: report.id })
    options.push(
      <PopoverMenu.RouterOption
        title={i18n.t('custom_reports.edit')}
        to={{ pathname: editRoute }}
      />
    )
  }

  if (report.authorId === currentUserId || (report.authorId && canShareReport)) {
    options.push(
      <PopoverMenu.TouchableOption
        title={i18n.t('custom_reports.share.title')}
        description=""
        onClick={onShare}
      />
    )
  }

  if (featureAvailability.available && canEditDashboard) {
    options.push(
      <PopoverMenu.RouterOption
        title={i18n.t('custom_reports.clone')}
        to={{ pathname: navigation.reports.id.clone.fullPath({ id: report.id }) }}
      />
    )
  }

  if (canEditDashboard) {
    options.push(
      <PopoverMenu.TouchableOption title={i18n.t('custom_reports.move_report')} onClick={onMove} />
    )
  }

  options.push(
    <PopoverMenu.TouchableOption
      title={i18n.t('custom_reports.download_csv')}
      onClick={onDownloadCsv}
    />,
    <PopoverMenu.TouchableOption
      title={i18n.t('custom_reports.download_excel')}
      onClick={onDownloadExcel}
    />,
    <PopoverMenu.TouchableOption
      title={i18n.t('custom_reports.download_txt')}
      onClick={onDownloadTxt}
    />
  )

  if (featureAvailability.available && !isSqlType && canEditReport) {
    options.push(
      <PopoverMenu.RouterOption
        title={i18n.t('custom_reports.open_sql')}
        description={
          reportStruct.hasFiltersWithVariables
            ? i18n.t('custom_reports.open_sql_not_available')
            : undefined
        }
        to={{ pathname: navigation.reports.id.openSql.fullPath({ id: report.id }) }}
        disabled={reportStruct.hasFiltersWithVariables}
      />
    )
  }

  if (canEditReport) {
    options.push(
      <PopoverMenu.TouchableOption title={i18n.t('common.remove')} onClick={onRemove} danger />
    )
  }

  return options
}

const Header = ({
  reportStruct,
  canEditDashboard,
  canEditReport,
  onMove,
  onShare,
  variables,
  reportsSharePolicy,
}: Props) => {
  const { access } = useCurrentSession()
  const [openDeprecateReferenceMessage, toggleDeprecateReferenceMessage] = useToggle(false)

  const onRemove = async () => {
    const report = reportStruct.props.resource
    await asyncAction(() => report.destroy())
  }

  const { onSubmit: onDownloadExcel } = useReportExportExcel({
    reportStruct,
    variables,
  })

  const { onSubmit: onDownloadCsv } = useReportExportCsv({
    reportStruct,
    variables,
  })

  const { onSubmit: onDownloadTxt } = useReportExportTxt({
    reportStruct,
    variables,
  })

  const options = useOptions(
    reportStruct,
    canEditReport,
    canEditDashboard,
    onDownloadCsv,
    onDownloadExcel,
    onDownloadTxt,
    onMove,
    onRemove,
    onShare,
    reportsSharePolicy,
    access.id
  )

  const report = reportStruct.props.resource

  const projections = reports.find({
    id: reportStruct.reportId,
  })?.projections

  const hasDeprecatedReference = projections?.find((p) => hasDeprecatedPath(p))
  return (
    <Box
      border={{
        bottom: {
          color: 'grey400',
          style: 'solid',
          width: 's1',
        },
      }}
      flexDirection="row"
      justifyContent="spaceBetween"
      alignItems="center"
      padding="s16"
    >
      <Box flexDirection="row" alignItems="center" gap="s8">
        <span className={'Header_title'}>{report.attributes.title}</span>
        <TooltipInfo reportIdentifier={report.identifier} />
        {hasDeprecatedReference && (
          <Tooltip.Dialog
            attachment="middle left"
            open={openDeprecateReferenceMessage}
            toggle={toggleDeprecateReferenceMessage}
            label={i18n.t('custom_reports.deprecated_sources_info')}
          >
            <Icon.Small icon="alert" encircled="grey800" />
          </Tooltip.Dialog>
        )}
      </Box>
      <Box flexDirection="row" alignItems="center" gap="s16">
        {report.sqlQuery !== null ? (
          <Tag leftItem="circle" type="radical" showCircle>
            SQL
          </Tag>
        ) : null}

        <PopoverMenu type="list">
          <PopoverMenu.Trigger asChild>
            <Button type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {options}
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
    </Box>
  )
}

export default observer(Header)
